import { getBackendUrl } from "@/utils/api";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Video, 
  Image, 
  Edit3, 
  Users, 
  Key,
  GraduationCap,
  Play,
  BarChart3,
  Sliders,
  RefreshCw,
  Camera,
  Clapperboard,
  Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useSiteStyles } from "../hooks/useSiteStyles";
import PageShell from "@/components/PageShell";
import axios from "axios";

const API = getBackendUrl();

const PricesPage = () => {
  const { styles } = useSiteStyles();
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/api/page-content/prices`);
      setPageContent(response.data);
    } catch (error) {
      console.error("Failed to fetch page content:", error);
    }
  };

  // Use CMS content or defaults
  const heroTitle = pageContent?.prices_hero_title || "Our Prices";
  const heroSubtitle = pageContent?.prices_hero_subtitle || "Transparent Pricing";
  const heroDescription = pageContent?.prices_hero_description || "Simple, straightforward pricing for professional property marketing. No hidden fees.";
  
  // Initial Setup pricing
  const setupTitle = pageContent?.prices_setup_title || "Initial Setup";
  const setupPrice = pageContent?.prices_setup_price || "£1,000";
  const setupDescription = pageContent?.prices_setup_description || "Everything you need to get your property campaign up and running.";
  const setupNote = pageContent?.setup_note || "* Customer supplies raw video and photo content";
  
  // Monthly pricing
  const monthlyTitle = pageContent?.prices_monthly_title || "Monthly Campaign";
  const monthlyPrice = pageContent?.prices_monthly_price || "£150";
  const monthlyDescription = pageContent?.prices_monthly_description || "Continuous campaign management to maximise your property's exposure.";
  
  // Videography pricing
  const videoTitle = pageContent?.prices_video_title || "Videography";
  const videoPrice = pageContent?.prices_video_price || "£500";
  const videoDescription = pageContent?.prices_video_description || "Don't have video content? We'll create it for you professionally.";
  const videoNote = pageContent?.video_note || "Add this to your initial setup if you need professional content creation.";

  // CTA
  const ctaTitle = pageContent?.prices_cta_title || "Ready to market your property?";
  const ctaDescription = pageContent?.prices_cta_description || "Get in touch to discuss your requirements and start your campaign.";

  const initialSetupFeatures = [
    { icon: Video, text: "Video preparation & editing" },
    { icon: Image, text: "Campaign stills preparation" },
    { icon: Edit3, text: "Professional editing (customer supplies raw footage)" },
    { icon: Users, text: "Account setup & configuration" },
    { icon: Key, text: "Provision of access credentials" },
    { icon: GraduationCap, text: "Full training session included" }
  ];

  const monthlyFeatures = [
    { icon: Play, text: "Campaign launch & management" },
    { icon: BarChart3, text: "Statistics monitoring" },
    { icon: Sliders, text: "Ongoing optimisation" },
    { icon: RefreshCw, text: "Campaign refresh cycles" }
  ];

  const videographyFeatures = [
    { icon: Camera, text: "Professional property photography" },
    { icon: Clapperboard, text: "High-quality video production" },
    { icon: Edit3, text: "Complete editing included" }
  ];

  return (
    <PageShell>
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Hero Section */}
      <section className="pt-8 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p 
            className="text-sm font-medium tracking-widest uppercase mb-4"
            style={{ color: styles?.primary_color }}
          >
            {heroSubtitle}
          </p>
          <h1 className="font-serif text-4xl md:text-5xl font-medium mb-6">
            {heroTitle}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {heroDescription}
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Initial Setup Card */}
            <div className="bg-white border-2 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow relative">
              <div 
                className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: styles?.primary_color || '#4a7c59' }}
              >
                One-time
              </div>
              <div 
                className="p-8 text-white"
                style={{ backgroundColor: styles?.primary_color || '#4a7c59' }}
              >
                <h3 className="font-serif text-xl mb-2">{setupTitle}</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-bold">{setupPrice}</span>
                </div>
                <p className="text-white/80 mt-2 text-sm">One-time setup fee</p>
              </div>
              <div className="p-8">
                <p className="text-muted-foreground mb-6">
                  {setupDescription}
                </p>
                <ul className="space-y-4">
                  {initialSetupFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: styles?.primary_color ? `${styles.primary_color}15` : '#4a7c5915' }}
                      >
                        <feature.icon 
                          className="w-3.5 h-3.5" 
                          style={{ color: styles?.primary_color || '#4a7c59' }}
                        />
                      </div>
                      <span className="text-sm">{feature.text}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t">
                  <p className="text-xs text-muted-foreground italic">
                    {setupNote}
                  </p>
                </div>
              </div>
            </div>

            {/* Monthly Campaign Card */}
            <div className="bg-white border-2 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow relative lg:scale-105 lg:-my-4">
              <div 
                className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                style={{ 
                  backgroundColor: styles?.accent_color ? `${styles.accent_color}20` : '#c9a55c20',
                  color: styles?.accent_color || '#c9a55c'
                }}
              >
                <Star className="w-3 h-3" />
                Popular
              </div>
              <div 
                className="p-8 text-white"
                style={{ backgroundColor: styles?.accent_color || '#c9a55c' }}
              >
                <h3 className="font-serif text-xl mb-2">{monthlyTitle}</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-bold">{monthlyPrice}</span>
                  <span className="text-white/80">/month</span>
                </div>
                <p className="text-white/80 mt-2 text-sm">Ongoing campaign management</p>
              </div>
              <div className="p-8">
                <p className="text-muted-foreground mb-6">
                  {monthlyDescription}
                </p>
                <ul className="space-y-4">
                  {monthlyFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: styles?.accent_color ? `${styles.accent_color}20` : '#c9a55c20' }}
                      >
                        <feature.icon 
                          className="w-3.5 h-3.5" 
                          style={{ color: styles?.accent_color || '#c9a55c' }}
                        />
                      </div>
                      <span className="text-sm">{feature.text}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="block mt-8">
                  <Button 
                    className="w-full rounded-none"
                    style={{ backgroundColor: styles?.accent_color || '#c9a55c' }}
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>

            {/* Videography Extra Card */}
            <div className="bg-white border-2 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow relative">
              <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                Add-on
              </div>
              <div className="p-8 text-white bg-gradient-to-br from-blue-500 to-blue-600">
                <h3 className="font-serif text-xl mb-2">{videoTitle}</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-bold">{videoPrice}</span>
                </div>
                <p className="text-white/80 mt-2 text-sm">Professional content creation</p>
              </div>
              <div className="p-8">
                <p className="text-muted-foreground mb-6">
                  {videoDescription}
                </p>
                <ul className="space-y-4">
                  {videographyFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-blue-100">
                        <feature.icon className="w-3.5 h-3.5 text-blue-600" />
                      </div>
                      <span className="text-sm">{feature.text}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t">
                  <p className="text-xs text-muted-foreground">
                    {videoNote}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Box */}
          <div className="mt-16 bg-muted/50 rounded-2xl p-8 md:p-12">
            <div className="max-w-3xl mx-auto">
              <h3 className="font-serif text-2xl font-medium mb-6 text-center">Package Summary</h3>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div className="bg-white rounded-lg p-6">
                  <p className="text-3xl font-bold" style={{ color: styles?.primary_color || '#4a7c59' }}>{setupPrice}</p>
                  <p className="text-sm text-muted-foreground mt-1">{setupTitle}</p>
                  <p className="text-xs text-muted-foreground">(one-time)</p>
                </div>
                <div className="bg-white rounded-lg p-6">
                  <p className="text-3xl font-bold" style={{ color: styles?.accent_color || '#c9a55c' }}>{monthlyPrice}</p>
                  <p className="text-sm text-muted-foreground mt-1">{monthlyTitle}</p>
                  <p className="text-xs text-muted-foreground">(recurring)</p>
                </div>
                <div className="bg-white rounded-lg p-6">
                  <p className="text-3xl font-bold text-blue-500">{videoPrice}</p>
                  <p className="text-sm text-muted-foreground mt-1">{videoTitle}</p>
                  <p className="text-xs text-muted-foreground">(optional add-on)</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <h3 className="font-serif text-2xl font-medium mb-4">{ctaTitle}</h3>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              {ctaDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  className="rounded-none px-8"
                  style={{ backgroundColor: styles?.primary_color }}
                >
                  Contact Us
                </Button>
              </Link>
              <Link to="/how-it-works">
                <Button variant="outline" className="rounded-none px-8">
                  Learn How It Works
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
    </PageShell>
  );
};

export default PricesPage;
